import {
  Paper,
  Group,
  Text,
  ThemeIcon,
  Tooltip,
  Skeleton,
  Stack,
} from "@mantine/core";
import { IconCurrencyDollar, IconInfoCircle } from "@tabler/icons-react";

export interface RevenueStatCardProps {
  recorded: number;
  pipeline: number;
  apiRecorded?: number;
  manualRecorded?: number;
  unattributedRecorded?: number;
  feesConfigured: boolean;
  isLoading?: boolean;
  monthLabel?: string;
}

const TOOLTIP_COPY =
  "Recorded = configured CPT charge value on cases whose Tebra encounter was recorded during the selected month, whether entered manually or by API. " +
  "Pending = configured CPT charge value on signed-off cases in a retryable attention state (always current, not month-bound). " +
  "These are charge values, not submitted claims, payments, or realized revenue.";

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

export function RevenueStatCard({
  recorded,
  pipeline,
  apiRecorded = 0,
  manualRecorded = 0,
  unattributedRecorded = 0,
  feesConfigured,
  isLoading,
  monthLabel,
}: RevenueStatCardProps) {
  return (
    <Paper withBorder p="md" radius="md" style={{ height: "100%" }}>
      <Group justify="space-between" mb="xs" wrap="nowrap">
        <Group gap={6} wrap="nowrap">
          <Text size="sm" c="dimmed" fw={500}>
            Charge value
          </Text>
          <Tooltip label={TOOLTIP_COPY} multiline w={300} withArrow>
            <IconInfoCircle
              size={14}
              style={{ color: "var(--mantine-color-gray-5)" }}
            />
          </Tooltip>
        </Group>
        <ThemeIcon variant="light" color="teal" size="lg">
          <IconCurrencyDollar size={24} />
        </ThemeIcon>
      </Group>
      {isLoading ? (
        <Skeleton height={48} mb={4} />
      ) : (
        <Stack gap={2} mt={4}>
          <Group justify="space-between" gap="xs">
            <Text size="xs" c="dimmed">
              Recorded in Tebra
            </Text>
            <Text size="md" fw={700}>
              {formatCurrency(recorded)}
            </Text>
          </Group>
          <Group justify="space-between" gap="xs">
            <Text size="xs" c="dimmed">
              Pending
            </Text>
            <Text size="md" fw={700} c="teal">
              {formatCurrency(pipeline)}
            </Text>
          </Group>
        </Stack>
      )}
      {!isLoading && (
        <Text size="xs" c="dimmed" mt={6}>
          Manual {formatCurrency(manualRecorded)} · API{" "}
          {formatCurrency(apiRecorded)}
          {unattributedRecorded > 0
            ? ` · Legacy ${formatCurrency(unattributedRecorded)}`
            : ""}
        </Text>
      )}
      {!isLoading && monthLabel && (
        <Text size="xs" c="dimmed" mt={6}>
          Recorded: {monthLabel}
        </Text>
      )}
      {!isLoading && !feesConfigured && (
        <Text size="xs" c="orange" mt={6}>
          CPT-code fees not yet configured
        </Text>
      )}
    </Paper>
  );
}
